/**
 * Pricing and buyer-fee math - the ONE place these are computed.
 *
 * The ring bids a single whole-dollar number; what it MEANS depends on the
 * lot's pricing mode, and every surface that shows money (clerk console, Auction
 * TV, the Smart Buyer Card, and eventually M6 settlement) must agree by
 * construction. `pricingView` used to live only in the console; it lives here
 * now so the buyer card and the future invoice can never disagree with the
 * board about a per-head total.
 */

import type { BidSource } from './types.js';

export interface PriceView {
  mode: string;
  perUnit: number | null;
  total: number | null;
  /** A short label for the board, e.g. "per head × 3" or "choice of 4". */
  label: string;
  /** True when the total differs from the bid (per-head with quantity > 1). */
  totalDiffers: boolean;
}

/**
 * Pricing modes (§11.5):
 *   PER_LOT   - the bid is the price for the whole lot.
 *   PER_HEAD  - the bid is price per animal; the lot total is bid × quantity
 *               (a mare with a foal sold by the head is not one hammer price).
 *   CHOICE    - the bid buys the buyer's CHOICE of the group at that price; the
 *               remaining lots re-offer (the re-offer itself is a future engine
 *               feature; the label is correct today).
 */
export function pricingView(mode: string, bid: number | null, quantity: number): PriceView {
  const q = Math.max(1, Math.floor(quantity) || 1);
  if (mode === 'PER_HEAD') {
    const total = bid === null ? null : bid * q;
    return { mode, perUnit: bid, total, label: q > 1 ? `per head × ${q}` : 'per head', totalDiffers: q > 1 && bid !== null };
  }
  if (mode === 'CHOICE') {
    return { mode, perUnit: bid, total: bid, label: q > 1 ? `choice of ${q}` : 'choice', totalDiffers: false };
  }
  return { mode: 'PER_LOT', perUnit: bid, total: bid, label: 'per lot', totalDiffers: false };
}

/**
 * Per-channel buyer's premium in basis points (bps). FLOOR bids pay the `live`
 * rate, ONLINE/PROXY the `online` rate. This is the documented shape of the
 * `sales.buyer_premium` jsonb (migration 0000: "percentages as basis points").
 * Nothing enforces it, so callers pass whatever the column holds and we treat a
 * missing rate as "not deterministically known".
 */
export interface BuyerPremium {
  // `| undefined` is explicit so zod-parsed configs (whose optional keys infer
  // as `number | undefined`) assign cleanly under exactOptionalPropertyTypes.
  live?: number | undefined;
  online?: number | undefined;
}

export interface FeeEstimate {
  /** True only when EVERY line's channel rate is deterministically known. When
   *  false, the UI must fall back to the hammer total and label it. */
  feesKnown: boolean;
  /** Whole dollars, rounded. Zero when feesKnown is false. */
  estFees: number;
}

/**
 * Seller-side commission (§ settlement). The consignor is paid the hammer gross
 * for their SOLD lots, minus the sale company's commission. `bps` is a flat
 * commission in basis points (e.g. 800 = 8%). Documented shape of the
 * `sales.seller_commission` jsonb / a per-consignor `defaults.sellerCommissionBps`
 * override. Tiered commissions, per-lot minimums, and other seller fees (photo,
 * entry, chute) are deferred - this is the flat-rate MVP.
 */
export interface SellerCommission {
  bps?: number | undefined; // explicit for exactOptionalPropertyTypes interop (see BuyerPremium)
}

export interface SellerProceeds {
  /** Sum of hammer line totals for the consignor's sold lots (cents). */
  grossCents: number;
  /** Commission withheld (cents). Zero when the rate isn't known. */
  commissionCents: number;
  /** What the consignor is paid: gross − commission (cents). */
  netCents: number;
  /** True only when a commission rate was deterministically known. When false,
   *  the statement shows gross as a CEILING and labels the commission unset. */
  commissionKnown: boolean;
}

/**
 * Seller proceeds for one consignor's sold lots. Cents in, cents out (the
 * `purchase` rows are already cents), so there is no dollar rounding drift with
 * the buyer side. A missing rate returns commissionKnown:false with net == gross
 * (never a guessed deduction) so the office resolves the rate before paying out.
 */
export function estimateSellerProceeds(
  lines: readonly { lineTotalCents: number }[],
  commissionBps: number | null | undefined,
): SellerProceeds {
  const grossCents = lines.reduce((s, l) => s + l.lineTotalCents, 0);
  if (commissionBps === undefined || commissionBps === null) {
    return { grossCents, commissionCents: 0, netCents: grossCents, commissionKnown: false };
  }
  const commissionCents = Math.round((grossCents * commissionBps) / 10_000);
  return { grossCents, commissionCents, netCents: grossCents - commissionCents, commissionKnown: true };
}

const channelOf = (source: BidSource): 'live' | 'online' => (source === 'FLOOR' ? 'live' : 'online');

/**
 * A DETERMINISTIC buyer-fee estimate - buyer's premium only. Taxes, premium
 * tiers, credits, deposits, and per-lot tax resolution are M6 settlement and
 * are deliberately NOT computed here. Returns feesKnown:false (and estFees 0)
 * when the premium doesn't define a rate for some purchased line's channel, so
 * the caller shows the hammer total instead of a wrong number.
 */
export function estimateBuyerFees(
  lines: readonly { bidSource: BidSource; lineTotal: number }[],
  premium: BuyerPremium | null | undefined,
): FeeEstimate {
  if (!premium || (premium.live === undefined && premium.online === undefined)) {
    return { feesKnown: false, estFees: 0 };
  }
  let fees = 0;
  let known = true;
  for (const l of lines) {
    const bps = channelOf(l.bidSource) === 'live' ? premium.live : premium.online;
    if (bps === undefined || bps === null) {
      known = false;
      continue;
    }
    fees += (l.lineTotal * bps) / 10_000;
  }
  return { feesKnown: known, estFees: Math.round(fees) };
}
